import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import Banner from "../components/Banner";
import RegisterCallout from "../components/RegisterCallout";
import {
  Agenda,
  AgendaDay,
  AgendaEvent,
  AgendaSpeaker,
  AgendaBreakoutLabel,
  AgendaWorkshop,
  AgendaWorkshopEvent,
} from "../components/Agenda";
import AgendaData27 from "../data/AgendaData27";
import { slugify } from "../utils/slugify";

export default function PageAgenda() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  }, [location]);

  const wedDay = AgendaData27.find((d) => d.title === "Wednesday, June 9");
  const workshopStartIndex = wedDay.events.findIndex((e) =>
    e.title?.includes("Workshop Participants"),
  );

  // Everything before the workshop lunch stays in the normal purple agenda
  const daysForAgenda = AgendaData27.map((day) =>
    day.title === wedDay.title
      ? { ...day, events: day.events.slice(0, workshopStartIndex) }
      : day,
  );

  // Lunch + the workshop session itself get pulled into their own white section
  const workshopSectionEvents = wedDay.events.slice(workshopStartIndex);

  return (
    <>
      <Banner
        leftImage="img/bg-banner-round-gradient.webp"
        className="bg-after-purple"
      >
        <div className="col-12 col-md-8">
          <div className="row">
            <div className="col-12 mb-3">
              <div className="display-1 color-white">Daily Agenda</div>
            </div>
            <div className="col-12 col-md-11 col-lg-10 col-xl-9 mb-2">
              <div style={{ maxWith: "40em" }}>
                <p className="color-white mb-0">
                  FinComp 2027 is proudly approved by both the Society for Human
                  Resource Management (SHRM) and the HR Certification Institute
                  (HRCI). Details coming soon.
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="col-4 text-end px-0 pb-4 d-none d-md-block">
          <img
            className="img-fluid mb-0"
            src="../img/logo-certs.webp"
            alt="HRCI and SHRM certifications"
          />
        </div>
      </Banner>
      <Agenda>
        {daysForAgenda.map((day, dayIndex) => (
          <AgendaDay key={dayIndex} title={day.title}>
            {day.events.map((event, eventIndex) => (
              <AgendaEvent key={eventIndex} time={event.time}>
                {event.speakers?.map((s, i) => (
                  <AgendaSpeaker
                    key={i}
                    name={s.name}
                    company={s.company}
                    isKeynote={event.isKeynote}
                  />
                ))}

                {!event.sessions?.length && event.title && (
                  <>
                    <div
                      id={slugify(event.title)}
                      className="anchor-offset"
                    ></div>
                    <h2 className="mb-2">{event.title}</h2>
                  </>
                )}

                {event.sessions?.map((session, i) => {
                  const speakers = session.speakers || [];
                  const multipleSpeakers = speakers.length > 1;

                  const speakerGrid = (
                    <div
                      className={
                        multipleSpeakers
                          ? "row row-cols-1 row-cols-md-2 g-3 mb-2"
                          : "mb-2"
                      }
                    >
                      {speakers.map(
                        (speaker, j) =>
                          speaker && (
                            <div
                              key={j}
                              className={multipleSpeakers ? "col" : ""}
                            >
                              <AgendaSpeaker
                                name={speaker.name}
                                company={speaker.company}
                                isKeynote={event.isKeynote}
                              />
                            </div>
                          ),
                      )}
                    </div>
                  );

                  return (
                    <div key={i}>
                      <AgendaBreakoutLabel count={session.count} />

                      {event.titleFirst && !session.count && event.title && (
                        <>
                          <div
                            id={slugify(event.title)}
                            className="anchor-offset"
                          ></div>
                          <h2 className="mb-2">{event.title}</h2>
                        </>
                      )}

                      {speakerGrid}

                      {!event.titleFirst && !session.count && event.title && (
                        <>
                          <div
                            id={slugify(event.title)}
                            className="anchor-offset"
                          ></div>
                          <h2 className="mb-2">{event.title}</h2>
                        </>
                      )}
                      {session.title && (
                        <>
                          <div
                            id={slugify(session.title)}
                            className="anchor-offset"
                          ></div>
                          <h2 className="mb-2">{session.title}</h2>
                        </>
                      )}

                      {session.description && (
                        <p
                          dangerouslySetInnerHTML={{
                            __html: session.description,
                          }}
                        />
                      )}
                      {i < event.sessions.length - 1 && <hr className="my-4" />}
                    </div>
                  );
                })}
              </AgendaEvent>
            ))}
          </AgendaDay>
        ))}
      </Agenda>

      <span id="post-conference-workshop"></span>

      <AgendaWorkshop title="Wednesday, June 9 - Post-Conference Workshop">
        {workshopSectionEvents.map((event, eventIndex) => (
          <AgendaWorkshopEvent key={eventIndex} time={event.time}>
            {!event.sessions?.length && event.title && (
              <>
                <div id={slugify(event.title)} className="anchor-offset"></div>
                <h2>{event.title}</h2>
                <strong>Orchid Terrace (1st level, outdoors)</strong>
              </>
            )}

            {event.sessions?.map((session, i) => {
              const speakers = session.speakers || [];
              const multipleSpeakers = speakers.length > 1;

              return (
                <div key={i}>
                  <div
                    className={
                      multipleSpeakers
                        ? "row row-cols-1 row-cols-md-2 g-3 mb-2"
                        : "mb-2"
                    }
                  >
                    {speakers.map(
                      (speaker, j) =>
                        speaker && (
                          <div
                            key={j}
                            className={multipleSpeakers ? "col" : ""}
                          >
                            <AgendaSpeaker
                              name={speaker.name}
                              company={speaker.company}
                            />
                          </div>
                        ),
                    )}
                  </div>

                  {event.title && (
                    <>
                      <div
                        id={slugify(event.title)}
                        className="anchor-offset"
                      ></div>
                      <h2 className="mb-2">{event.title}</h2>
                    </>
                  )}

                  {session.description && (
                    <p
                      dangerouslySetInnerHTML={{ __html: session.description }}
                    />
                  )}
                </div>
              );
            })}
          </AgendaWorkshopEvent>
        ))}
      </AgendaWorkshop>

      <RegisterCallout />
    </>
  );
}
